/**
 * Interview Practice Service
 *
 * Frontend API calls for the virtual mock-interview feature.
 * Follows the same auth + fetch pattern as resumeTuner.ts.
 */

import { getFunctions } from 'firebase/functions';
import { getAuth } from 'firebase/auth';
import { getEmulatorHost, isUsingEmulators } from './emulator';
import {
  InterviewDuration,
  InterviewSession,
  InterviewDebrief,
  StartInterviewResponse,
  SubmitAnswerResponse,
  GenerateDebriefResponse,
} from '../types/interviewPractice';

/* ------------------------------------------------------------------ */
/*  Base URL helper (shared pattern)                                   */
/* ------------------------------------------------------------------ */

function getFunctionsBaseUrl(): string {
  const functions = getFunctions();
  const projectId =
    functions.app.options.projectId ||
    process.env.REACT_APP_FIREBASE_PROJECT_ID ||
    'postmortem-pheonix';
  const region = functions.region || 'us-central1';

  if (isUsingEmulators()) {
    const host = getEmulatorHost();
    const port = process.env.REACT_APP_FUNCTIONS_EMULATOR_PORT || '5001';
    return `http://${host}:${port}/${projectId}/${region}`;
  }

  return `https://${region}-${projectId}.cloudfunctions.net`;
}

async function getIdToken(): Promise<string> {
  const user = getAuth().currentUser;
  if (!user) throw new Error('Must be authenticated');
  return user.getIdToken(isUsingEmulators());
}

/* ------------------------------------------------------------------ */
/*  1. Start interview — generates questions                           */
/* ------------------------------------------------------------------ */

export async function startInterviewPractice(
  jobTitle: string,
  company: string,
  jobDescription: string,
  durationMinutes: InterviewDuration,
): Promise<InterviewSession> {
  const token = await getIdToken();
  const baseUrl = getFunctionsBaseUrl();

  const res = await fetch(`${baseUrl}/startInterviewPractice`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ jobTitle, company, jobDescription, durationMinutes }),
  });

  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const data: StartInterviewResponse = await res.json();
  if (!data.success || !data.session) throw new Error(data.error || 'Failed to start interview');
  return data.session;
}

/* ------------------------------------------------------------------ */
/*  2. Submit answer for a question                                    */
/* ------------------------------------------------------------------ */

export async function submitInterviewAnswer(
  sessionId: string,
  questionId: string,
  answer: string,
  durationSec: number,
): Promise<SubmitAnswerResponse> {
  const token = await getIdToken();
  const baseUrl = getFunctionsBaseUrl();

  const res = await fetch(`${baseUrl}/submitInterviewAnswer`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ sessionId, questionId, answer, durationSec }),
  });

  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

/* ------------------------------------------------------------------ */
/*  3. Generate debrief / retro                                        */
/* ------------------------------------------------------------------ */

export async function generateInterviewDebrief(
  sessionId: string,
  voiceTranscript?: string,
): Promise<InterviewDebrief> {
  const token = await getIdToken();
  const baseUrl = getFunctionsBaseUrl();

  const body: Record<string, any> = { sessionId };
  if (voiceTranscript) body.voiceTranscript = voiceTranscript;

  const res = await fetch(`${baseUrl}/generateInterviewDebrief`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const data: GenerateDebriefResponse = await res.json();
  if (!data.success || !data.debrief) throw new Error(data.error || 'Failed to generate debrief');
  return data.debrief;
}
